import * as qs from 'qs'
import type { Ref } from 'vue'
import type { EventDescription } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import {
  buildTransactionalPageShowcaseSearchParts,
  TRANSACTIONAL_PAGE_SIMPLE_SHOWCASE_LIMIT,
  TRANSACTIONAL_PAGE_SIMPLE_SHOWCASE_ORDER_BY,
} from '~/utils/transactionalPageShowcase'

interface EventListPageSimpleShowcaseParams {
  pageType: EventListPageTypeString
  locationSlug: string | undefined
  categorySlug: string
  eventListPageData: Ref<StrapiEventListPage>
}

const showcaseImageUrl = (event: EventDescription): string => {
  const principal = eventGetters.getPrincipalImage(event)
  if (principal?.cropped) return principal.cropped
  const firstThumb = eventGetters.getFirstThumb(event)
  if (firstThumb) return firstThumb
  return eventGetters.getOpenGraphImage(event)
}

const firstTagSlugFromStrapi = (
  page: StrapiEventListPage | undefined
): string | undefined => {
  const tags = page?.searchFilters?.tags
  const value = tags?.map((t) => t.value).find(Boolean)
  return value?.toString()
}

export const useEventListPageSimpleShowcase = ({
  pageType,
  locationSlug,
  categorySlug,
  eventListPageData,
}: EventListPageSimpleShowcaseParams) => {
  const config = useRuntimeConfig()
  const { locale, t } = useI18n()
  const localePath = useLocalePath()
  const isSubdomain = useIsSubdomain()
  const nuxt = useNuxtApp()

  const localeBCP47 = computed(() => convertLocaleIntoBCP47(locale.value))
  const localeISO2 = computed(() => convertISO2Locale(locale.value))

  const kftHeaders = computed(() => ({
    'Content-Type': 'application/json',
    'Accept-Language': `${localeBCP47.value},${localeISO2.value}`,
  }))

  const asyncKey = computed(() => {
    const tag = firstTagSlugFromStrapi(eventListPageData.value)
    return [
      'elp-simple-showcase',
      pageType,
      locationSlug ?? '',
      categorySlug,
      tag ?? '',
      locale.value,
      isSubdomain.value ? 'emb' : 'kft',
    ].join(':')
  })

  return useAsyncData(
    asyncKey,
    async () => {
      const publishedSearchToken = isSubdomain.value
        ? 'embeddedPublished:1'
        : 'konfettiPublished:1'

      const searchParts = buildTransactionalPageShowcaseSearchParts({
        pageType,
        locationSlug,
        categorySlug,
        tagFilterSlug: firstTagSlugFromStrapi(eventListPageData.value),
        publishedSearchToken,
      })

      const listUrl = `v1/store/events?${qs.stringify(
        {
          limit: TRANSACTIONAL_PAGE_SIMPLE_SHOWCASE_LIMIT,
          search: searchParts.join(';'),
          orderBy: TRANSACTIONAL_PAGE_SIMPLE_SHOWCASE_ORDER_BY,
          sortedBy: 'desc',
          include: ['supplier'],
        },
        { encode: false, arrayFormat: 'comma' }
      )}`

      const listResponse = await $fetch<
        KftApiDefaultResponse<EventDescription[]>
      >(listUrl, {
        baseURL: config.public.apiBaseURL as string,
        headers: kftHeaders.value,
      })

      const rows = listResponse?.data ?? []
      if (!rows.length) return []

      const linkText = t('eventListPage.simpleShowcase.learnMore')

      return rows.map((ev) => {
        const permalink = ev.permalink || ''
        const link = localePath(
          eventGetters.isBoxEvent(ev) ? `/k/${permalink}/` : `/e/${permalink}/`
        )
        return {
          title: eventGetters.getSupplierName(ev) || ev.title || '',
          description: ev.subtitle?.trim() || '',
          imageUrl: showcaseImageUrl(ev),
          link,
          linkText,
        }
      })
    },
    {
      default: () => [],
      server: true,
      lazy: false,
      dedupe: 'defer',
      watch: [asyncKey, eventListPageData],
      getCachedData(key) {
        return nuxt.isHydrating ? nuxt.payload.data[key] : nuxt.static.data[key]
      },
    }
  )
}
